import { useTranslation } from 'react-i18next';
import React, {useEffect, useRef, useState} from 'react';
import {Controller} from "react-hook-form";
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import {Editor} from "@tinymce/tinymce-react";
import {useParams} from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import {find, get, join} from 'lodash';
import UploadIcon from '@mui/icons-material/Upload';
import {useUploadFileMutation} from "../../app/apis/upload.api";
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';


const SupportListItem = ({item, list, index, onChange, onEditItem}) => {
  const {lang} = useParams();
  const editorRefEn = useRef(null);
  const editorRefFr = useRef(null);
  const [initTextEn, setInitTextEn] = useState("");
  const [initTextFr, setInitTextFr] = useState("");


  useEffect(()=> {
    setInitTextEn(item.narrative && item.narrative[0]?.value ? item.narrative[0].value : "");
    setInitTextFr(item.narrative && item.narrative[1]?.value ? item.narrative[1].value : "");
  },[])

  const onEditorChange = (content, langIndex) => {
    let tmp = [...list];
    let narrative = tmp[index].narrative;
    narrative[langIndex] = {
      lang: langIndex === 0 ? "en" : "fr",
      value: content
    }
    tmp[index] = {...tmp[index], narrative};
    onChange(tmp);
  }

  const onDelete = () => {
    let tmp = [...list];
    tmp.splice(index, 1);
    onChange(tmp);
  }

  const plugins = ['lists', 'link', 'image', 'code'];

  return <Grid container  style={{borderBottom: "1px solid gray"}} mb={2}>
    <Grid item xs={6}>
      <Editor
          tinymceScriptSrc={process.env.PUBLIC_URL + '/tinymce/tinymce.min.js'}
          onInit={(evt, editor) => editorRefEn.current = editor}
          initialValue={initTextEn}
          init={{
            height: 280,
            menubar: true,
            promotion: false,
            branding: false,
            plugins: join(plugins, ' '),
            toolbar: 'undo redo | formatselect | ' +
                'bold italic backcolor | alignleft aligncenter ' +
                'alignright alignjustify | bullist numlist outdent indent | ' +
                'removeformat | link image code',
            content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
            language: lang === 'fr' && 'fr_FR'
          }}
          onEditorChange={(content) => onEditorChange(content, 0)}
      />
    </Grid>
    <Grid item xs={6}>
      <Editor
          tinymceScriptSrc={process.env.PUBLIC_URL + '/tinymce/tinymce.min.js'}
          onInit={(evt, editor) => editorRefFr.current = editor}
          initialValue={initTextFr}
          init={{
            height: 280,
            menubar: true,
            promotion: false,
            branding: false,
            plugins: join(plugins, ' '),
            toolbar: 'undo redo | formatselect | ' +
                'bold italic backcolor | alignleft aligncenter ' +
                'alignright alignjustify | bullist numlist outdent indent | ' +
                'removeformat | link image code',
            content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
            language: lang === 'fr' && 'fr_FR',
          }}
          onEditorChange={(content) => onEditorChange(content, 1)}
      />
    </Grid>
    <Grid item xs={12}>
      <Box><a target={"_blank"} href={item.uri}>{item.desc[0].value} - {item.desc[1].value}</a></Box>
      <EditIcon sx={{fontSize: 14, cursor:"pointer"}} onClick={()=>onEditItem(index)}/>
      <DeleteForeverOutlinedIcon sx={{fontSize: 14, cursor:"pointer"}} onClick={onDelete}/>
    </Grid>
  </Grid>
}

const SupportList = ({name, ...restProps}) => {
  const {t} = useTranslation();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState({});
  const uploadInputRef = useRef(null);
  const [uploadFile] = useUploadFileMutation();

  const onAddItem = () => {
    setDialogContent({
      uri: "",
      descriptionEn:"",
      descriptionFr:"",
    });
    setOpen(true);
  }

  const onEditItem = (i) => {
    setDialogContent({
      index: i,
      descriptionEn: find(get(list[i], 'desc'), r => r.lang === "en")?.value,
      descriptionFr: find(get(list[i], 'desc'), r => r.lang === "fr")?.value,
      uri: list[i].uri
    });
    setOpen(true);
  }
  const onSaveItem = (onChange) => {
    let data = {
      format: "file",
      lang: "en",
      uri: dialogContent.uri,
      desc: [
        {lang: "en", value: dialogContent.descriptionEn},
        {lang: "fr", value: dialogContent.descriptionFr},
      ],
      narrative: [{lang: "en", value: ""}, {lang: "fr", value: ""}]
    };


    if (dialogContent.index >= 0) {
      let tmp = [...list];
      tmp[dialogContent.index] = {...tmp[dialogContent.index], ...data};
      onChange(tmp);
    } else {
      onChange([...list, data])
    }
    setOpen(false);
  }

  const onChangeUpload = (e) => {
    let selectedFile = e.target.files[0];
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('uploadType', 'file');

    uploadFile(formData)
        .then((resp) => {
          setDialogContent({
            ...dialogContent,
            descriptionEn: selectedFile.name,
            descriptionFr: selectedFile.name,
            uri:resp.data.temporaryLocation
          });
        });
  }

  const onCloseDialog = (onChange) => {
    setOpen(false);

    if(dialogContent.index === null || dialogContent.index===undefined) {
      let data = {
        format: "file",
        lang: "en",
        uri: '',
        desc: [
          {lang: "en", value: ''},
          {lang: "fr", value: ''},
        ],
        narrative: [{lang: "en", value: ""}, {lang: "fr", value: ""}]
      };

      onChange([...list, data])
    }

    setOpen(false);
  }


  return (
      <Controller
          name={name}
          render={({field}) => {

            if (field?.value)
              setList(field.value)

            return (
                <>
                  <Grid container>
                    <Grid item xs={6}>
                      English introduction
                    </Grid>
                    <Grid item xs={6}>
                      French introduction
                    </Grid>
                    {list.map((e, i) => <Grid item xs={12} key={i}>
                      <SupportListItem item={e} index={i} list={list} onChange={field.onChange} onEditItem={onEditItem}/>
                    </Grid>)}
                    <Grid item xs={12}>
                      <span onClick={onAddItem} style={{color: '#04629A', cursor: 'pointer'}}>
                        <AddCircleOutlineOutlinedIcon sx={{fontSize: 14}}/>
                        Add new support document
                      </span>
                    </Grid>
                  </Grid>
                  <Dialog
                      open={open}
                      onClose={() => onCloseDialog(field.onChange)}
                  >
                    <Box p={2}>
                      <Stack direction={'column'} spacing={2}>
                        <TextField
                            label={'URL or ONE Reference'}
                            inputProps={{style: {fontSize: 12}}}
                            size="small"
                            value={dialogContent.uri}
                            onChange={(e) => setDialogContent({...dialogContent, uri: e.target.value})}
                        />
                        <TextField
                            label={'Description - En'}
                            inputProps={{style: {fontSize: 12}}}
                            size="small"
                            value={dialogContent.descriptionEn}
                            onChange={(e) => setDialogContent({...dialogContent, descriptionEn: e.target.value})}
                        />
                        <TextField
                            label={'Description - Fr'}
                            inputProps={{style: {fontSize: 12}}}
                            size="small"
                            value={dialogContent.descriptionFr}
                            onChange={(e) => setDialogContent({...dialogContent, descriptionFr: e.target.value})}
                        />
                      </Stack>

                      <Stack direction={'row'} spacing={1} mt={2}>
                        <Button variant="contained" onClick={()=>uploadInputRef.current.click()}><UploadIcon size="small"/></Button>
                        <Button variant="contained" onClick={() => onSaveItem(field.onChange)}>{t('save')}</Button>
                        <Button variant="contained" onClick={() => onCloseDialog(field.onChange)}>{t('close')}</Button>
                      </Stack>
                      <input
                          type='file'
                          onChange={onChangeUpload}
                          style={{display: 'none'}}
                          accept='.doc,.docx,.pdf,.xml,.html,.htm'
                          ref={uploadInputRef}
                      />
                    </Box>
                  </Dialog>
                </>
            )
          }}
      />
  )
};

export default SupportList;
